import { getApp } from 'firebase/app';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { getAuth } from 'firebase/auth';

// ── Design constants ──────────────────────────────────────────────────────────
export const ROLE_COLORS = {
  mp: '#0e9cc0', embedded: '#7F77DD', system: '#1D9E75',
  ops: '#BA7517', experimental: '#D85A30', finance: '#378ADD',
  obc: '#639922', admin: '#65d4fa', pe: '#8b5cf6', mentor: '#a855f7',
  payload: '#0891b2', c_suite: '#dc2626',
};

export const QUICK_TAGS = {
  mp:           ['GMAT','Trajectory','Simulation','Mission design','Coverage analysis'],
  embedded:     ['PCB','Firmware','Schematic','Testing','Power analysis','RP2040'],
  system:       ['CAD','Thermal','FEA','Structure','Mass budget'],
  obc:          ['OBC','Firmware','Integration','BOM','Module test'],
  experimental: ['Experiment','Results','Measurement','Calibration','Data'],
  ops:          ['Operations','Meeting','Planning','Site visit','Coordination'],
  admin:        ['Admin','Management','Strategy','Finance','HR'],
  pe:           ['AI','Prompt','Review','Quality','Testing'],
  finance:      ['Budget','Expense','Invoice','Payroll','Report'],
  mentor:       ['Review','Guidance','Meeting','Feedback','Strategy'],
  payload:      ['Mass budget','ICD','Integration','Environmental','Test','Verification'],
  c_suite:      ['Board','Strategy','Investor','Compliance','Governance'],
};

export const MOODS = ['😊','🔥','😤','😴','✅','🚀'];

// ── Date helpers ──────────────────────────────────────────────────────────────
export function todayIST() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
}

export function dateLabel(dateStr) {
  const today = todayIST();
  const yesterday = new Date(Date.now() - 86400000).toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
  if (dateStr === today) return 'TODAY';
  if (dateStr === yesterday) return 'YESTERDAY';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' }).toUpperCase();
}

export function relTime(ts) {
  if (!ts?.toDate) return '';
  const diff = Date.now() - ts.toDate().getTime();
  const min = Math.floor(diff / 60000);
  if (min < 2) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 7) return `${d}d ago`;
  return ts.toDate().toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

// ── Compiler selection (localStorage) ────────────────────────────────────────
const SEL_KEY = 'smc_compiler_selection';

export function getCompilerSelection() {
  try { return new Set(JSON.parse(localStorage.getItem(SEL_KEY) || '[]')); } catch { return new Set(); }
}

export function setCompilerSelection(ids) {
  localStorage.setItem(SEL_KEY, JSON.stringify([...ids]));
}

export function clearCompilerSelection() {
  localStorage.removeItem(SEL_KEY);
}

// ── Image compression ─────────────────────────────────────────────────────────
export function compressImage(file, maxWidth = 1200, quality = 0.82) {
  if (file.size < 200 * 1024) return Promise.resolve(file);
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const ratio = Math.min(1, maxWidth / img.width);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * ratio);
      canvas.height = Math.round(img.height * ratio);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(blob => {
        if (!blob) { resolve(file); return; }
        const out = new File([blob], file.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' });
        resolve(out.size < file.size ? out : file);
      }, 'image/jpeg', quality);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}

// ── Video thumbnail ───────────────────────────────────────────────────────────
export function generateVideoThumbnail(file) {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);
    video.src = url;
    video.muted = true;
    video.currentTime = 1;
    const cleanup = () => URL.revokeObjectURL(url);
    video.onseeked = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || 320;
        canvas.height = video.videoHeight || 240;
        canvas.getContext('2d').drawImage(video, 0, 0);
        cleanup();
        resolve(canvas.toDataURL('image/jpeg', 0.7));
      } catch { cleanup(); resolve(null); }
    };
    video.onerror = () => { cleanup(); resolve(null); };
  });
}

// ── Geolocation ───────────────────────────────────────────────────────────────
export async function detectLocation() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) { reject(new Error('Geolocation not supported')); return; }
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const { latitude: lat, longitude: lng } = pos.coords;
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
          { headers: { 'User-Agent': 'SpacemanCraft-OS/1.0' } }
        );
        const data = await res.json();
        const a = data.address || {};
        const city = a.city || a.town || a.village || a.county || '';
        const state = a.state || '';
        const name = [city, state].filter(Boolean).join(', ') || data.display_name?.split(',').slice(0, 2).join(', ') || 'Location detected';
        resolve({ lat, lng, name });
      } catch {
        resolve({ lat, lng, name: `${lat.toFixed(4)}, ${lng.toFixed(4)}` });
      }
    }, (err) => reject(err));
  });
}

// ── Firebase Storage upload ───────────────────────────────────────────────────
export function uploadMedia(updateId, file, type, onProgress) {
  const storage = getStorage(getApp());
  const folder = type === 'image' ? 'images' : type === 'video' ? 'videos' : 'files';
  const filename = `${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
  const path = `daily_updates/${updateId}/${folder}/${filename}`;
  const storageRef = ref(storage, path);

  return new Promise((resolve, reject) => {
    const task = uploadBytesResumable(storageRef, file);
    task.on('state_changed',
      snap => onProgress?.((snap.bytesTransferred / snap.totalBytes) * 100),
      reject,
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        resolve({ path, url, filename, type, size: file.size });
      }
    );
  });
}

// ── Claude API proxy (via Cloud Function) ────────────────────────────────────
const SYSTEM_PROMPT = `You are a technical documentation assistant for Spacemancraft Private Limited, a space technology startup developing the EMMS/EP-ADCS propulsion system for the SPACEMANCRAFT-1 12U CubeSat. Generate a structured technical report from the provided daily activity updates. Use formal engineering language. Preserve all specific measurements, component names (DRV8874, RP2040, VL53L1X, etc.), dates, and locations exactly. Format the report with these sections: Title, Date Range, Summary, Detailed Activities (grouped by date and topic), Key Findings & Measurements, Decisions Made, Next Steps & Action Items. Do not add information not present in the updates.`;

export async function runCompilerReport({ updates, reportType, portalLabel, onChunk, onDone, onError }) {
  const updatesText = updates.map(u =>
    `[${u.date}] ${u.authorName} (${u.authorRole}):\n${u.text}\nLocation: ${u.location?.name || 'Not specified'}\nTags: ${(u.tags || []).join(', ')}`
  ).join('\n\n---\n\n');

  const userMessage = `Report type: ${reportType}\nPortal: ${portalLabel}\nMission: SPACEMANCRAFT-1\n\nDaily updates to compile:\n\n${updatesText}\n\nGenerate the structured technical report now.`;

  try {
    const token = await getAuth(getApp()).currentUser?.getIdToken();
    if (!token) { onError?.('Not signed in.'); return; }

    const res = await fetch('/api/claude', {
      method:  'POST',
      headers: {
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        model:      'claude-sonnet-4-6',
        max_tokens: 2000,
        system:     SYSTEM_PROMPT,
        messages:   [{ role: 'user', content: userMessage }],
      }),
    });

    const data = await res.json();
    if (!res.ok) {
      onError?.(data.error || `Server error ${res.status}`);
      return;
    }

    const fullText = data.text || '';
    if (!fullText) { onError?.('Empty response from server.'); return; }

    // Typewriter: reveal ~25 chars per 8 ms to simulate streaming
    let i = 0;
    const tick = () => {
      if (i >= fullText.length) { onDone?.(); return; }
      onChunk(fullText.slice(i, i + 25));
      i += 25;
      setTimeout(tick, 8);
    };
    tick();
  } catch (ex) {
    onError?.(ex.message || 'Generation failed');
  }
}
